import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import {
  Message, Button, Confirm, Dimmer, Loader,
} from 'semantic-ui-react';

import LoadingDataEnhancer from './LoadingData';
import badgingBlacklistsQuery from '../graphql/badgingBlacklistsQuery';
import createBadgingBlacklistMutation from '../graphql/createBadgingBlacklistMutation';
import deleteBadgingBlacklistMutation from '../graphql/deleteBadgingBlacklistMutation';
import updateBadgingBlacklistMutation from '../graphql/updateBadgingBlacklistMutation';

import BadgingBlacklistsNew from '../views/BadgingBlacklists/New';
import BadgingBlacklistsEdit from '../views/BadgingBlacklists/Edit';
import BadgingBlacklistsList from '../views/BadgingBlacklists/List';
import Search from '../views/Shared/Search';
import Pagination from '../views/Shared/Pagination';
import { setSearch } from '../data/searchField/actions';

const PAGE_SIZE = 25;
const FIRST_PAGE = 1;

const BadgingBlacklists = ({
  data,
  createMutation,
  deleteMutation,
  updateMutation,
  searchFields,
  setSearchFields: setSearchFieldsProp,
}) => {
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [selectedBadgingBlacklist, setSelectedBadgingBlacklist] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [newFormError, setNewFormError] = useState(null);
  const [newFormSubmitting, setNewFormSubmitting] = useState(false);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormSubmitting, setEditFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchVariablesRef = useRef({ vin: null, sellerNumber: null });

  const onSearchSubmitHandler = event => {
    const { VIN, sellerID } = event.target;
    const sellerNumber = (sellerID.value || '').trim() || null;
    const variableArguments = {
      vin: (VIN.value || '').trim() || null,
      sellerNumber,
    };
    searchVariablesRef.current = variableArguments;
    setCurrentPage(FIRST_PAGE);
    setIsSearching(true);
    data
      .refetch({ ...variableArguments, page: FIRST_PAGE, pageSize: PAGE_SIZE })
      .finally(() => setIsSearching(false));
    setSearchFieldsProp({ vin: variableArguments.vin, sellerID: sellerNumber });
  };

  const onSelectBadgingBlacklist = (action, badgingBlacklist) => {
    setSelectedBadgingBlacklist(badgingBlacklist);
    switch (action) {
      case 'Edit':
        setIsEditModalOpen(true);
        break;
      case 'Delete':
        setIsDeleteConfirmationOpen(true);
        break;
      default:
        setSelectedBadgingBlacklist(null);
    }
  };

  const openNewForm = () => setIsNewModalOpen(true);

  const onNewFormCloseHandler = () => {
    setNewFormError(null);
    setNewFormSubmitting(false);
    setIsNewModalOpen(false);
  };

  const onNewFormSubmitHandler = values => {
    setNewFormSubmitting(true);
    createMutation({
      variables: {
        input: {
          badgingBlacklist: {
            vin: values.vin,
            sellerNumber: values.sellerNumber || null,
            expirationDate: values.expirationDate,
            notes: values.notes || null,
          },
        },
      },
    })
      .then(({ data: responseData }) => {
        const {
          createBadgingBlacklist: { success, errors },
        } = responseData;
        if (success) {
          setNewFormSubmitting(false);
          setFlashMessage('Successfully created badging blacklist entry');
          data.refetch({
            ...searchVariablesRef.current,
            page: currentPage,
            pageSize: PAGE_SIZE,
          });
          onNewFormCloseHandler();
        } else {
          setNewFormSubmitting(false);
          setNewFormError(
            (errors && errors[0])
              || 'Error while creating badging blacklist entry'
          );
        }
      })
      .catch(() => {
        setNewFormSubmitting(false);
        setNewFormError('Error while creating badging blacklist entry');
      });
  };

  const onEditFormCloseHandler = () => {
    setEditFormError(null);
    setEditFormSubmitting(false);
    setIsEditModalOpen(false);
  };

  const onEditFormSubmitHandler = values => {
    setEditFormSubmitting(true);
    updateMutation({
      variables: {
        input: {
          badgingBlacklistId: selectedBadgingBlacklist.id,
          badgingBlacklist: {
            expirationDate: values.expirationDate,
            notes: values.notes || null,
          },
        },
      },
    })
      .then(({ data: responseData }) => {
        const {
          updateBadgingBlacklist: { success, errors },
        } = responseData;
        if (success) {
          setEditFormSubmitting(false);
          setFlashMessage('Successfully updated badging blacklist entry');
          data.refetch({
            ...searchVariablesRef.current,
            page: currentPage,
            pageSize: PAGE_SIZE,
          });
          onEditFormCloseHandler();
        } else {
          setEditFormSubmitting(false);
          setEditFormError(
            (errors && errors[0])
              || 'Error while updating badging blacklist entry'
          );
        }
      })
      .catch(() => {
        setEditFormSubmitting(false);
        setEditFormError('Error while updating badging blacklist entry');
      });
  };

  const handleDeleteConfirm = () => {
    deleteMutation({
      variables: { input: { badgingBlacklistId: selectedBadgingBlacklist.id } },
    })
      .then(({ data: responseData }) => {
        const {
          deleteBadgingBlacklist: { success },
        } = responseData;
        if (success) {
          setFlashMessage('Successfully deleted badging blacklist entry');
          data.refetch({
            ...searchVariablesRef.current,
            page: currentPage,
            pageSize: PAGE_SIZE,
          });
        } else {
          setFlashMessage('Failed to delete badging blacklist entry');
        }
        setIsDeleteConfirmationOpen(false);
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
        setFlashMessage('Failed to delete badging blacklist entry');
      });
  };

  const handleDeleteCancel = () => setIsDeleteConfirmationOpen(false);

  const handleDismiss = () => setFlashMessage(null);

  const goToNextPage = () => {
    const badgingBlacklists = data.badgingBlacklists || {};
    const total = badgingBlacklists.total || 0;
    const nextPage = currentPage + 1;
    if (currentPage * PAGE_SIZE < total) {
      setCurrentPage(nextPage);
      data.refetch({
        ...searchVariablesRef.current,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > FIRST_PAGE) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      data.refetch({
        ...searchVariablesRef.current,
        page: prevPage,
        pageSize: PAGE_SIZE,
      });
    }
  };

  const { badgingBlacklists, error } = data;
  const items = (badgingBlacklists && badgingBlacklists.items) || [];
  const total = (badgingBlacklists && badgingBlacklists.total) || 0;
  const start = items.length > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const end = start > 0 ? start + items.length - 1 : 0;
  const hasNext = end < total;

  return (
    <div>
      {error && (
        <Message
          negative
          content={error.message || 'An error occurred while loading data.'}
        />
      )}
      {flashMessage && (
        <Message onDismiss={handleDismiss} content={flashMessage} positive />
      )}
      <Button color='green' onClick={openNewForm} inverted>
        {' '}
        Create New Badging Blacklist
      </Button>

      <br />
      <br />

      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['VIN', 'sellerID']}
        multipleSearches
      />

      {isNewModalOpen && (
        <div>
          <BadgingBlacklistsNew
            isModalOpen={isNewModalOpen}
            onCloseHandler={onNewFormCloseHandler}
            onSubmitHandler={onNewFormSubmitHandler}
            error={newFormError}
            submitting={newFormSubmitting}
          />
        </div>
      )}

      {isEditModalOpen && (
        <div>
          <BadgingBlacklistsEdit
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            error={editFormError}
            submitting={editFormSubmitting}
            badgingBlacklist={selectedBadgingBlacklist}
          />
        </div>
      )}

      <Dimmer.Dimmable dimmed={isSearching}>
        <Dimmer active={isSearching} inverted>
          <Loader>Searching...</Loader>
        </Dimmer>

        <BadgingBlacklistsList
          onSelectBadgingBlacklist={onSelectBadgingBlacklist}
          badgingBlacklists={items}
        />

        <Pagination
          start={start}
          end={end}
          nextHandler={goToNextPage}
          previousHandler={goToPreviousPage}
          loading={data.loading}
          lastPage={!hasNext && items.length !== 0}
          pageSize={PAGE_SIZE}
        />
      </Dimmer.Dimmable>

      <Confirm
        open={isDeleteConfirmationOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        content='Are you sure you want to delete this badging blacklist entry?'
      />
    </div>
  );
};

BadgingBlacklists.propTypes = {
  data: PropTypes.shape({
    refetch: PropTypes.func.isRequired,
    badgingBlacklists: PropTypes.shape({
      total: PropTypes.number,
      items: PropTypes.array,
    }),
    error: PropTypes.object,
    loading: PropTypes.bool,
  }).isRequired,
  createMutation: PropTypes.func.isRequired,
  deleteMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  searchFields: PropTypes.object,
  setSearchFields: PropTypes.func.isRequired,
};

BadgingBlacklists.defaultProps = {
  searchFields: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return { searchFields };
};

export default compose(
  withRouter,
  graphql(badgingBlacklistsQuery, {
    options: {
      fetchPolicy: 'network-only',
      variables: { page: FIRST_PAGE, pageSize: PAGE_SIZE },
    },
  }),
  graphql(createBadgingBlacklistMutation, { name: 'createMutation' }),
  graphql(deleteBadgingBlacklistMutation, { name: 'deleteMutation' }),
  graphql(updateBadgingBlacklistMutation, { name: 'updateMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(BadgingBlacklists);
